import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Component, createRef, } from 'react';
import { Link } from 'react-router-dom';
import { Spin, notification } from 'antd';
import { effect, signal, } from '@preact/signals-react';
import { throttle } from 'throttle-debounce';
import { sessionContext } from 'components/Authenticated';
import * as ProjectAPI from 'api/project-api';
import { fetchQueuedProjectsIds } from 'api/transcription-api';
import { MessageStream } from 'libs/media-processing/message-stream';
import MediaProcessingHandler from 'libs/media-processing/media-processing-handler';
import TranscriptionProgressHandler from 'libs/media-processing/transcription-progress-handler';
import MediaHandler from 'libs/media-processing/media-handler';
import ProjectErrorHandler from 'libs/media-processing/project-error-handler';
import * as clientEnv from 'libs/client-env';
import { txt } from 'libs/i18n';
import * as TeamAPI from 'api/team-api';
import MediaSource from 'libs/media-processing/media-source';
import { Mutex } from 'async-mutex';
import CircleButton from 'components/CircleButton';
import { SettingOutlined } from '@ant-design/icons';
import { BeeyLocale } from 'libs/locales/locale';
import { getAllAbbreviations } from 'api/expandable-abbreviations-api';
import { fetchHeadingsMetadata, getLanguageFromTrsx } from 'api/project-api';
import clsx from 'clsx';
import { ApiError } from '@newtontechnologies/beey-api-js-client/receivers';
import deepEqual from 'fast-deep-equal';
import dayjs from 'dayjs';
import { WebsocketUpload } from 'libs/media-processing/websocket-upload';
import { nanoid } from 'nanoid';
import { loggerService } from 'libs/logger';
import AlertPage from '../AlertPage';
import LoadingPage from '../LoadingPage';
import DashboardPage from '../../components/DashboardPage';
import EditorKeyboardControls from './EditorKeyboardControls';
import MediaControls from './MediaControls';
import MemoizedEditorToolbar from './EditorToolbar';
import EditorController from './DocumentEditor/editor-controller';
import EditorContent from './EditorContent';
import MemoizedEditorSettings from './EditorSettings';
import WithMediaPlayback from './contexts/playback';
import Gamepad from './Gamepad';
import Playback, { PlaybackEvents } from './MediaPlayer/playback';
import SoundWave from './SoundWave';
import MemoizedProjectMenuPanel from './ProjectMenuPanel';
import { loadAiAssignments } from './signalsStore/aiAssignments';
import { SubModeTutorial, SubModeTutorialContext, getSubModeTutorialSteps } from './contexts/subModeTutorial';
import { savingStateSignal, setSavingStateSignal } from './signalsStore/savingState';
import { setTranscriptionSourceSignal, transcriptionSourceSignal } from './signalsStore/transcriptionSource';
import styles from './style.module.less';
import ProblemsDialog from './ProblemsDialog';
import { EditorContext, projectStateSignal, setProjectStateSignal, } from './contexts/projectSignal';
import MicControls from './MicControls';
import NoRecordingBanner from './NoRecordingBanner';
const HOLDER_KEY = 'project-current-holder';
const DEFAULT_LANGUAGE_CODE = 'en-US';
const saveCurrentUserToSessionStorage = (holder) => {
    sessionStorage.setItem(HOLDER_KEY, JSON.stringify(holder));
};
const loadCurrentUserFromSessionStorage = () => {
    const holder = sessionStorage.getItem(HOLDER_KEY);
    if (holder === null) {
        return null;
    }
    try {
        return JSON.parse(holder);
    }
    catch (e) {
        return null;
    }
};
export default class EditorPage extends Component {
    constructor(props, context) {
        super(props, context);
        this.editorController = null;
        this.secondaryEditorController = null;
        this.projectSignal = signal(null);
        this.uploader = null;
        this.cachedContextValue = null;
        this.handleTakeOverProject = async () => {
            var _a;
            const { lock } = this.state;
            const { session } = this.context;
            const projectState = projectStateSignal.value;
            const { isCaptionMode } = this.state;
            if (projectState.phase !== 'loaded') {
                return;
            }
            await this.messageStream.takeOverExclusiveAccess(this.currentUser, lock.holder);
            this.setState({ lock: { isLocked: false, holder: this.currentUser } });
            let trsxResult = await ProjectAPI.fetchTrsx(session.connection, projectState.project.id, 'currentTrsx');
            if (!trsxResult.isSuccess() && trsxResult.err() === 'not-found') {
                trsxResult = await ProjectAPI.fetchTrsx(session.connection, projectState.project.id, 'partialTrsx');
            }
            if (trsxResult.isSuccess()) {
                const trsx = trsxResult.get();
                (_a = this.editorController) === null || _a === void 0 ? void 0 : _a.importTrsx({
                    content: trsx.content,
                    etag: trsx.etag,
                    label: this.editorController.label,
                    language: getLanguageFromTrsx(trsx.content),
                }, true, projectState.project, !isCaptionMode);
            }
            else {
                notification.error({ message: txt('trsxDlFailed') });
                global.logger.error('Failed to fetch trsx', { error: trsxResult.err() });
            }
        };
        this.enqueueProjectUpdate = async (action) => {
            const release = await this.projectChangeMutex.acquire();
            try {
                // NOTE: We need to make sure that the project passed to action is up to date.
                // The component launching the action may have older version that got outdated
                // while awaiting mutex acquire.
                if (projectStateSignal.value.phase === 'loaded') {
                    const updatedProject = await Promise.race([
                        action(projectStateSignal.value.project),
                        // NOTE: Sometimes the project update queue got stuck forever.
                        // This timeout prevents that - if we wait too long, the action will fail
                        // and the queue will get unstuck.
                        new Promise((resolve, reject) => {
                            setTimeout(() => { reject(new Error('timeout when updating project')); }, 40000);
                        }),
                    ]);
                    await this.refreshProject(updatedProject);
                }
                else {
                    global.logger.error('cannot do action on project', { project: projectStateSignal.value });
                }
                release();
            }
            catch (error) {
                release();
                global.logger.error('failed to update project', {}, error);
                throw error;
            }
        };
        this.initializeProjectMessageStream = (project, location) => {
            const { session } = this.context;
            const { connection } = session;
            const handleProjectUpdateMessage = async ({ data }) => {
                if (data.changed === 'KeywordsHighlight' || data.changed === 'Tags') {
                    void this.refreshProject();
                }
                else if (data.changed === 'Checklist') {
                    const projectChecklist = await this.fetchChecklist(project.id);
                    this.setState((prevState) => ({
                        checklist: Object.assign(Object.assign({}, prevState.checklist), { problems: projectChecklist }),
                    }));
                }
            };
            const syncEditorLanguageWithProject = () => {
                var _a, _b, _c, _d;
                if (projectStateSignal.value.phase !== 'loaded') {
                    return;
                }
                const language = (_b = (_a = projectStateSignal.value.project.transcriptionConfig) === null || _a === void 0 ? void 0 : _a.language) !== null && _b !== void 0 ? _b : DEFAULT_LANGUAGE_CODE;
                (_c = this.editorController) === null || _c === void 0 ? void 0 : _c.setLanguage((_d = BeeyLocale.fromCode(language)) !== null && _d !== void 0 ? _d : BeeyLocale.const(DEFAULT_LANGUAGE_CODE));
            };
            const handleChainControlMessage = async () => {
                if (projectStateSignal.value.phase !== 'loaded') {
                    return;
                }
                if (projectStateSignal.value.project.transcriptionConfig === null) {
                    await this.refreshProject();
                }
                syncEditorLanguageWithProject();
            };
            const handleExclusiveAccessMessage = ({ data }) => {
                const serverHolder = JSON.parse(data.exclusiveAccessToken);
                const sameHolder = serverHolder.email === this.currentUser.email
                    && serverHolder.token === this.currentUser.token;
                if (sameHolder) {
                    this.setState({ lock: { isLocked: false, holder: this.currentUser } });
                }
                else {
                    this.setState({ lock: { isLocked: true, holder: serverHolder } });
                }
            };
            this.messageStream = new MessageStream(connection, project.id, { email: this.currentUser.email, token: this.currentUser.token });
            this.messageStream.addMessageListener('ProjectUpdates', 'Progress', handleProjectUpdateMessage);
            this.messageStream.addMessageListener('ChainControl', 'ChainCommand', handleChainControlMessage);
            this.messageStream.addMessageListener('ExclusiveAccess', 'Progress', handleExclusiveAccessMessage);
            this.mediaProcessingHandler = new MediaProcessingHandler(this.messageStream);
            this.mediaProcessingHandler.addEventListener('status-update', this.handleUpdateMediaProcessingStatus);
            this.mediaProcessingHandler.projectReloaded(project);
            this.transcriptionProgressHandler = new TranscriptionProgressHandler(this.messageStream);
            this.transcriptionProgressHandler.addEventListener('status-update', throttle(2000, this.handleUpdateTranscriptionProgress));
            this.transcriptionProgressHandler.projectReloaded(project);
            this.mediaHandler = new MediaHandler(this.messageStream);
            this.mediaHandler.addEventListener('status-update', this.handleUpdateMediaSource);
            this.mediaHandler.projectReloaded(project);
            if (location.state !== undefined && !this.mediaHandler.getStatus().isAvailable()) {
                // NOTE: We have newly created local project
                this.mediaHandler.useLocalSource(project, location.state.mediaFile);
            }
            this.projectErrorHandler = new ProjectErrorHandler(this.messageStream, this.refreshProject);
            this.projectErrorHandler.addEventListener('status-update', this.handleServerProjectError);
            this.projectErrorHandler.projectReloaded(project);
        };
        this.handleUpdateMediaProcessingStatus = (status, oldStatus) => {
            if (status === 'transcribing') {
                this.setState({
                    mediaProcessingStatus: status,
                    projectQueuePosition: null,
                });
            }
            else {
                this.setState({ mediaProcessingStatus: status });
            }
            if (status === 'completed' && oldStatus === 'words-arriving') {
                this.handleUpdateTranscribedMinutes();
            }
        };
        this.handleUpdateTranscriptionProgress = (transcriptionProgressStatus) => this.setState({ transcriptionProgressStatus });
        this.handleUpdateMediaSource = (mediaSource) => {
            const { mediaSource: oldMediaSource } = this.state;
            if (!deepEqual(oldMediaSource, mediaSource)) {
                this.setState({ mediaSource });
            }
        };
        this.handleServerProjectError = (serverProjectErrorStatus) => {
            const { projectErrorStatus } = this.state;
            if (projectErrorStatus === 'no-error') {
                this.setState({ projectErrorStatus: serverProjectErrorStatus });
            }
        };
        this.handleTranscriptionLoadFailed = () => {
            const { projectErrorStatus } = this.state;
            if (projectErrorStatus === 'no-error') {
                this.setState({
                    projectErrorStatus: {
                        category: 'Client',
                        reason: 'TrsxLoadFail',
                    },
                });
            }
        };
        this.setErrorStatus = (status) => {
            this.setState({ projectErrorStatus: status });
        };
        this.displayMessage = (type, text, duration) => {
            const { message } = this.props;
            if (type === 'info') {
                void message.info(text, duration);
            }
            if (type === 'error') {
                void message.error(text, duration);
            }
            if (type === 'success') {
                void message.success(text, duration);
            }
            if (type === 'warning') {
                void message.warning(text, duration);
            }
        };
        this.handlePlaybackDurationLoaded = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const timestamp = urlParams.get('timestamp');
            if (timestamp !== null) {
                this.playback.seekTo(Number(timestamp));
            }
        };
        this.updateLeavingPrevented = (prevent) => {
            const { history } = this.props;
            if (this.leavingPrevented === prevent)
                return;
            const unblock = history.block((_, action) => {
                if (transcriptionSourceSignal.peek() === 'mic') {
                    // eslint-disable-next-line no-alert
                    const confirmLeave = window.confirm(txt('confirmMicTranscriptionLeave'));
                    if (confirmLeave) {
                        void this.stopMicTranscription();
                        return undefined;
                    }
                    return false;
                }
                if (action === 'POP') {
                    return `${txt('confirmLeaving')} ${txt('unsavedChanges')}`;
                }
                return undefined;
            });
            if (!prevent) {
                unblock();
            }
            this.leavingPrevented = prevent;
            if (prevent) {
                window.addEventListener('beforeunload', this.beforeUnloadHandler);
            }
            else {
                window.removeEventListener('beforeunload', this.beforeUnloadHandler);
            }
        };
        this.beforeUnloadHandler = (e) => {
            var _a;
            if (transcriptionSourceSignal.peek() === 'mic') {
                e.preventDefault();
                (_a = this.uploader) === null || _a === void 0 ? void 0 : _a.close();
                this.uploader = null;
                setTranscriptionSourceSignal('file');
            }
            e.preventDefault();
            // Chrome requires returnValue to be set
            // eslint-disable-next-line no-param-reassign
            e.returnValue = '';
        };
        this.handleUpdateTranscribedMinutes = () => {
            const { refetchLogin } = this.context;
            void refetchLogin();
        };
        this.handleCriticalEditorError = () => {
            // eslint-disable-next-line no-alert
            alert(txt('criticalEditorError'));
            this.leaveEditor();
        };
        this.leaveEditor = () => {
            const { history } = this.props;
            history.push('/projects');
        };
        this.refreshProject = async (newProject) => {
            const projectState = projectStateSignal.value;
            const { session } = this.context;
            if (newProject === undefined) {
                if (projectState.phase !== 'loaded') {
                    throw new Error(`unexpected project value: ${String(projectState)}`);
                }
                const serverProject = await ProjectAPI.fetchProject(session.connection, projectState.project.id);
                if (serverProject === 'not-found') {
                    setProjectStateSignal({ phase: 'not-found' });
                    return;
                }
                await this.refreshProject(serverProject);
                return;
            }
            this.mediaHandler.projectReloaded(newProject);
            projectStateSignal.value = { phase: 'loaded', project: Object.assign({}, newProject) };
        };
        this.refreshProjectFiles = async () => {
            const projectState = projectStateSignal.value;
            const { session } = this.context;
            if (projectState.phase !== 'loaded') {
                return null;
            }
            const projectFiles = await ProjectAPI.fetchProjectFiles(session.connection, projectState.project.id);
            this.setState({ projectFiles });
            return projectFiles;
        };
        this.handleOpenSettings = () => {
            if (this.playback.playing) {
                this.playback.pause();
            }
            this.setState({ settingsOpened: true });
        };
        this.handleCloseSettings = () => {
            this.setState({ settingsOpened: false });
        };
        this.stopTranscription = async () => {
            const projectState = projectStateSignal.value;
            const { message } = this.props;
            const { session } = this.context;
            if (projectState.phase !== 'loaded') {
                throw new Error(`unexpected project value: ${String(projectState)}`);
            }
            setProjectStateSignal({ phase: 'deleting' });
            // First we need to close the message stream to not
            // receive confusing failed messages during project deletion
            this.closeProjectMessageStream();
            const deleteResult = await ProjectAPI.deleteProject(session.connection, projectState.project);
            if (deleteResult.isSuccess()) {
                this.leaveEditor();
                void this.handleUpdateTranscribedMinutes();
                void message.success(txt('transcriptionStopped'));
            }
            else {
                void message.error(txt('transcriptionStopFailed'));
            }
        };
        this.setAndSaveWaveCanvasVisibility = (isVisible) => {
            this.setWaveCanvasVisibility(isVisible);
            clientEnv.setIsWaveVisible(isVisible);
        };
        this.setWaveCanvasVisibility = (isVisible) => {
            this.setState({ isWaveCanvasVisible: isVisible });
        };
        this.setWaveCanvas = (waveCanvas) => {
            this.setState({ waveCanvas });
        };
        this.stopMicTranscription = async () => {
            var _a;
            (_a = this.uploader) === null || _a === void 0 ? void 0 : _a.close();
            this.uploader = null;
            await this.refreshProject();
            const { mediaSource } = this.state;
            if (mediaSource.isAvailable()) {
                this.setState({ projectErrorStatus: 'no-error' });
            }
            setTranscriptionSourceSignal('file');
        };
        this.state = {
            secondaryProjectState: { phase: 'loading' },
            projectFiles: null,
            settingsOpened: false,
            checklist: {
                problems: {
                    totalCount: 0,
                    listedCount: 0,
                    list: [],
                },
                modalOpened: false,
            },
            isCaptionMode: false,
            projectQueuePosition: null,
            isWaveCanvasVisible: clientEnv.getIsWaveVisible(),
            waveCanvas: null,
            mediaProcessingStatus: 'loading-project',
            transcriptionProgressStatus: 'not-in-progress',
            projectErrorStatus: 'no-error',
            mediaSource: MediaSource.NONE,
            lock: {
                isLocked: false,
                holder: { email: '', token: '' },
            },
        };
        this.currentUser = { email: '', token: '' };
        this.playback = new Playback(0, 0);
        this.playback.oneTimeEventListener(PlaybackEvents.DurationUpdate, this.handlePlaybackDurationLoaded);
        this.caretTimeRef = createRef();
        // @ts-ignore
        this.caretTimeRef.current = 0;
        this.projectChangeMutex = new Mutex();
    }
    componentDidMount() {
        const { session } = this.context;
        const { match: { params: { projId } } } = this.props;
        const storedHolder = loadCurrentUserFromSessionStorage();
        const newHolder = storedHolder !== null && storedHolder !== void 0 ? storedHolder : { email: session.login.user.email, token: nanoid(10) };
        saveCurrentUserToSessionStorage(newHolder);
        this.currentUser = newHolder;
        void this.loadProject(Number(projId));
        effect(() => {
            void transcriptionSourceSignal.value;
            this.projectSignal.value = projectStateSignal.value.phase === 'loaded' ? projectStateSignal.value.project : null;
            this.forceUpdate();
        });
        effect(() => {
            const savingState = savingStateSignal.value;
            this.updateLeavingPrevented(savingState !== 'saved' || transcriptionSourceSignal.peek() === 'mic');
        });
    }
    componentDidUpdate() {
        var _a, _b;
        const { session } = this.context;
        const { isWaveCanvasVisible } = this.state;
        if (projectStateSignal.value.phase === 'loaded' && projectStateSignal.value.project.isReadOnly === true && isWaveCanvasVisible) {
            this.setWaveCanvasVisibility(false);
        }
        try {
            (_a = this.editorController) === null || _a === void 0 ? void 0 : _a.updateSettings(session.login.user.settings);
        }
        catch (error) {
            global.logger.error('failed to update settings in editor', {}, error);
        }
        if (projectStateSignal.value.phase === 'loaded') {
            (_b = this.editorController) === null || _b === void 0 ? void 0 : _b.updateMediaStartDatetime(projectStateSignal.value.project.start);
            this.projectSignal.value = projectStateSignal.value.project;
        }
    }
    componentWillUnmount() {
        var _a;
        if (projectStateSignal.value.phase === 'loaded' || projectStateSignal.value.phase === 'deleting') {
            this.updateLeavingPrevented(false);
            this.closeProjectMessageStream();
            (_a = this.editorController) === null || _a === void 0 ? void 0 : _a.destroy();
            setSavingStateSignal('saved');
        }
        setProjectStateSignal({ phase: 'loading' });
    }
    render() {
        var _a, _b;
        const { projectErrorStatus, projectQueuePosition, mediaSource, projectFiles, secondaryProjectState, settingsOpened, checklist, mediaProcessingStatus, transcriptionProgressStatus, isCaptionMode, lock, } = this.state;
        const projectState = projectStateSignal.value;
        if (projectState.phase === 'loading') {
            return _jsx(LoadingPage, {});
        }
        if (projectState.phase === 'deleting') {
            return _jsx(LoadingPage, { message: txt('deletingProject') });
        }
        if (projectState.phase === 'no-last') {
            return (_jsx(AlertPage, { title: txt('noProj'), header: txt('noLastProj'), children: _jsxs("p", { children: [`${txt('noLastProjMsg')} `, _jsx(Link, { to: "/projects", children: txt('backToProjs') })] }) }));
        }
        if (projectState.phase === 'not-found') {
            return (_jsxs(AlertPage, { title: txt('notFound'), header: txt('projNotFound'), children: [_jsx("p", { children: txt('projNotFoundMsg') }), _jsx(Link, { to: "/projects", children: txt('backHome') })] }));
        }
        if (projectState.phase === 'load-error') {
            return (_jsxs(AlertPage, { title: txt('error'), header: txt('error'), children: [_jsx("p", { children: projectState.message }), _jsx(Link, { to: "/projects", children: txt('backHome') })] }));
        }
        if (this.editorController === null) {
            return _jsx(LoadingPage, {});
        }
        const { isWaveCanvasVisible, waveCanvas } = this.state;
        const isRightToLeft = (_b = (_a = this.editorController.getLanguage()) === null || _a === void 0 ? void 0 : _a.isRightToLeft()) !== null && _b !== void 0 ? _b : false;
        const { hasRecording } = projectState.project;
        // NOTE: This is useMemo equivalent to memoize the context value
        const projectId = projectState.project.id;
        if (this.cachedContextValue === null
            || this.cachedContextValue.projectSignal.value.id !== projectId) {
            this.cachedContextValue = {
                projectSignal: this.projectSignal,
                logger: loggerService.getRoot().child({ projectId }),
            };
        }
        const contextValue = this.cachedContextValue;
        return (_jsx(EditorContext.Provider, { value: contextValue, children: _jsxs(DashboardPage, { title: `${projectState.project.description.name} | Beey`, toolbar: (_jsx(MemoizedEditorToolbar, { onProjectUpdated: this.refreshProject, progressStatus: transcriptionProgressStatus, projectErrorStatus: projectErrorStatus, onStopTranscription: this.stopTranscription, enqueueProjectUpdate: this.enqueueProjectUpdate })), extraControls: (_jsxs(_Fragment, { children: [checklist.problems.totalCount !== 0 && (_jsx(CircleButton, { className: styles.buttonHighlight, onClick: () => this.setState({ checklist: Object.assign(Object.assign({}, checklist), { modalOpened: true }) }), content: checklist.problems.totalCount, tooltip: txt('settingsLabel'), tooltipPlacement: "bottomLeft" })), _jsx(CircleButton, { onClick: this.handleOpenSettings, content: _jsx(SettingOutlined, {}), tooltip: txt('settingsLabel'), tooltipPlacement: "bottomLeft" })] })), children: [_jsx(MemoizedEditorSettings, { opened: settingsOpened, onClose: this.handleCloseSettings, editorController: this.editorController }), _jsxs(SubModeTutorial, { tutorialSteps: getSubModeTutorialSteps(), children: [_jsxs(WithMediaPlayback, { mediaSource: mediaSource, playback: this.playback, captions: this.editorController.captions, projectErrorStatus: projectErrorStatus, children: [_jsx(Gamepad, {}), _jsx(SubModeTutorialContext.Consumer, { children: ({ retrieveTutorialState }) => (this.editorController === null ? _jsx(Spin, {})
                                            : (_jsxs(EditorKeyboardControls, { className: styles.editor, waveCanvas: waveCanvas, replayCaption: this.editorController.captions.handleReplayCaption, hasRecording: hasRecording, children: [hasRecording === false && (_jsx(NoRecordingBanner, {})), transcriptionSourceSignal.value === 'file' && hasRecording ? (_jsx(MediaControls, { mediaStart: projectState.project.description.start !== null
                                                            ? dayjs(projectState.project.description.start)
                                                            : null, caretTimeRef: this.caretTimeRef, isWaveVisible: isWaveCanvasVisible || retrieveTutorialState() === 'running', onWaveVisibilityChange: this.setAndSaveWaveCanvasVisibility, captions: this.editorController.captions, enqueueProjectUpdate: this.enqueueProjectUpdate })) : transcriptionSourceSignal.value === 'mic' ? (_jsx(MicControls, { uploader: this.uploader, onStop: this.stopMicTranscription, language: this.editorController.getLanguage() })) : null, hasRecording && ((isWaveCanvasVisible && isCaptionMode) || retrieveTutorialState() === 'running') ? (_jsx(SoundWave, { editorController: this.editorController, onWaveVisibilityChange: this.setWaveCanvasVisibility, setWaveCanvas: this.setWaveCanvas })) : null, _jsxs("div", { className: clsx(styles.editorWithSider, { [styles.documentRtl]: isRightToLeft }), children: [_jsx(MemoizedProjectMenuPanel, { mediaProcessingStatus: mediaProcessingStatus, mediaSource: mediaSource, isCaptionMode: isCaptionMode, editorController: this.editorController, enqueueProjectUpdate: this.enqueueProjectUpdate, isWaveVisible: (isCaptionMode && isWaveCanvasVisible) || retrieveTutorialState() === 'running', onWaveVisibilityChange: this.setWaveCanvasVisibility, isLocked: lock.isLocked, projectFiles: projectFiles }), _jsx(EditorContent, { mediaSource: mediaSource, 
                                                                // NOTE: secondaryProject is handled for backward compatibility
                                                                // and can be deleted later:
                                                                secondaryProject: secondaryProjectState.phase === 'loaded' ? Object.assign(Object.assign({}, secondaryProjectState.project), { isReadOnly: true }) : null, projectFiles: projectFiles, onProjectUpdated: this.refreshProject, editorController: this.editorController, secondaryEditorController: this.secondaryEditorController, mediaProcessingStatus: mediaProcessingStatus, messageStream: this.messageStream, projectErrorStatus: projectErrorStatus, projectQueuePosition: projectQueuePosition, onTranscriptionLoadFailed: this.handleTranscriptionLoadFailed, onErrorStatusChange: this.setErrorStatus, isCaptionMode: isCaptionMode, onLeaveEditorRequested: this.leaveEditor, enqueueProjectUpdate: this.enqueueProjectUpdate, lock: lock, onTakeOverProject: this.handleTakeOverProject, onRefreshProjectFiles: this.refreshProjectFiles })] })] }))) })] }), checklist.modalOpened && (_jsx(ProblemsDialog, { projectId: projectState.project.id, checklist: checklist.problems.list, onClose: () => this.setState({ checklist: Object.assign(Object.assign({}, checklist), { modalOpened: false }) }), onHighlightProblem: (time) => { var _a; return (_a = this.editorController) === null || _a === void 0 ? void 0 : _a.selectWrapline(time); } }))] })] }) }));
    }
    closeProjectMessageStream() {
        this.mediaProcessingHandler.destroy();
        this.transcriptionProgressHandler.destroy();
        this.mediaHandler.destroy();
        this.projectErrorHandler.destroy();
    }
    async fetchChecklist(projectId) {
        const { session } = this.context;
        return ProjectAPI.fetchChecklist(session.connection, projectId);
    }
    async fetchProjectQueuePosition(connection, project) {
        if (project.processingState !== 'InProgress') {
            return null;
        }
        const queuedIds = await fetchQueuedProjectsIds(connection);
        const index = queuedIds.findIndex((id) => id === project.id);
        return index === -1 ? null : index + 1;
    }
    async loadProject(projectId) {
        var _a, _b, _c, _d;
        const { history, location, message } = this.props;
        const { checklist } = this.state;
        const { session, updateUserSettings } = this.context;
        const { connection, login: { user } } = session;
        const userSettings = user.settings;
        let openId = projectId;
        if (openId === undefined || Number.isNaN(openId)) {
            if (userSettings.lastProjId === null) {
                setProjectStateSignal({ phase: 'no-last' });
                return;
            }
            openId = Number(userSettings.lastProjId);
            history.replace(`/edit/${openId}`);
        }
        try {
            if (openId !== Number(userSettings.lastProjId)) {
                updateUserSettings({ lastProjId: String(openId) });
            }
            const project = await ProjectAPI.fetchProject(connection, openId);
            if (project === 'not-found') {
                setProjectStateSignal({ phase: 'not-found' });
                return;
            }
            if (project.deleteAt !== undefined && !session.login.hasClaim('projects:showTrashed')) {
                setProjectStateSignal({ phase: 'not-found' });
                return;
            }
            if (transcriptionSourceSignal.peek() === 'mic') {
                this.projectSignal.value = project;
                this.uploader = new WebsocketUpload(connection, project, session.login.hasClaim('micTranscription:drafts'));
            }
            const projectQueuePosition = await this.fetchProjectQueuePosition(connection, project);
            const projectChecklist = await this.fetchChecklist(project.id);
            const headingsMetadata = await fetchHeadingsMetadata(connection, project);
            const team = await TeamAPI.fetchCurrentTeam(connection);
            const { sectionTagsAllowed } = team.settings;
            this.editorController = new EditorController(session, this.caretTimeRef, this.playback, (value) => { this.setState({ isCaptionMode: value }); }, this.handleCriticalEditorError, this.displayMessage, sectionTagsAllowed, project.isReadOnly, headingsMetadata, project.hasRecording);
            const language = (_c = (_b = (_a = project.transcriptionConfig) === null || _a === void 0 ? void 0 : _a.language) !== null && _b !== void 0 ? _b : session.login.user.settings.transcriptionSettings.recognitionLocale) !== null && _c !== void 0 ? _c : DEFAULT_LANGUAGE_CODE;
            this.editorController.setLanguage((_d = BeeyLocale.fromCode(language)) !== null && _d !== void 0 ? _d : BeeyLocale.const(DEFAULT_LANGUAGE_CODE));
            this.editorController.summary.setEnabled(team.settings.summariesAllowed);
            if (session.login.hasClaim('editor:abbreviations') === true) {
                const result = await getAllAbbreviations(session.connection);
                if (result.isSuccess()) {
                    this.editorController.expandableAbbreviations.setEnabled(true);
                    this.editorController.expandableAbbreviations.setAbbreviationsList(result.get());
                }
                else {
                    void message.error(txt('failedToFetchAbbreviations'));
                }
            }
            if (session.login.hasClaim('aitools:all')) {
                void loadAiAssignments(connection);
            }
            this.initializeProjectMessageStream(project, location);
            setProjectStateSignal({ phase: 'loaded', project });
            const projectFiles = await ProjectAPI.fetchProjectFiles(connection, project.id);
            this.setState({
                projectQueuePosition,
                checklist: Object.assign(Object.assign({}, checklist), { problems: projectChecklist }),
                projectFiles,
            });
            const secondaryProjectIdMetadata = await ProjectAPI.fetchSecondaryProjectId(connection, project);
            const secondaryProjectId = secondaryProjectIdMetadata !== null && secondaryProjectIdMetadata !== void 0 ? secondaryProjectIdMetadata : (new URLSearchParams(window.location.search)).get('secondaryProjectId');
            // NOTE: secondaryProject is handled for backward compatibility
            // and can be deleted later:
            if (secondaryProjectId !== null) {
                const secondaryProject = await ProjectAPI.fetchProject(session.connection, Number(secondaryProjectId));
                if (secondaryProject === 'not-found') {
                    this.setState({ secondaryProjectState: { phase: 'not-found' } });
                    global.logger.error('failed to load secondary project.');
                    return;
                }
                this.setState({
                    secondaryProjectState: { phase: 'loaded', project: secondaryProject },
                });
                this.secondaryEditorController = new EditorController(session, {}, this.playback, () => { }, () => { }, () => { }, sectionTagsAllowed, true, null, secondaryProject.hasRecording);
            }
            // NOTE: End of obsolete code
            // NOTE We always create secondary editor controller - it will be used when
            // a new transcription on existing project is started.
            this.secondaryEditorController = new EditorController(session, {}, this.playback, () => { }, () => { }, () => { }, sectionTagsAllowed, true, null, project.hasRecording);
            // Trigger re-render to pass secondaryEditorController to EditorContent
            this.forceUpdate();
        }
        catch (error) {
            global.logger.error('Failed to open project', {}, error);
            if (error instanceof ApiError) {
                setProjectStateSignal({ phase: 'load-error', message: String(error) });
            }
        }
    }
}
EditorPage.contextType = sessionContext;
